import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'

import { colors } from 'Constants'
import { Flex } from 'lese'

import request from 'Root/request'

import Card from 'Components/Card'
import { AutoComplete } from 'Components/Input'
import DateSelector from 'Components/DateSelector'
import Switch from 'react-switch'
import { SectionHeader } from 'Components/Section'
import Button from 'Components/Button'

const ModalCard = styled(Card)`
  background: ${colors.background};
  min-width: 350px;
`

const ErrorMessage = styled.span`
  color: #f00;
`

const Label = styled(Flex)``
Label.withComponent('label')

const createValidater = ({ validater, errorMessage, setError, setValid }) => {
  return (toValidate, onlyPositive) => {
    const isValid = validater(toValidate)
    if (onlyPositive) {
      if (isValid) setError('')
      setValid(isValid)
      return isValid
    }

    if (!isValid) setError(errorMessage)
    else setError('')

    setValid(isValid)
    return isValid
  }
}

export default ({
  baseUrl,
  isCluster,
  isOffice,
  isPatient,
  isDevice,
  includeJourneySelector,
  color,
  ...props
}) => {
  const [appCode, setAppCode] = useState('')
  const [office, setOffice] = useState('')
  const [patientRef, setPatientRef] = useState('')
  const [device, setDevice] = useState('')
  const [journey, setJourney] = useState('')
  const [journeyId, setJourneyId] = useState('')
  const [dates, setDates] = useState({})
  const [showArchivedOffices, setShowArchivedOffices] = useState(undefined)

  const [appCodeError, setAppCodeError] = useState('')
  const [officeError, setOfficeError] = useState('')
  const [patientRefError, setPatientRefError] = useState('')
  const [deviceError, setDeviceError] = useState('')
  const [journeyError, setJourneyError] = useState('')

  const [appCodeSuggestions, setAppCodeSuggestions] = useState([])
  const [officeSuggestions, setOfficeSuggestions] = useState([])
  const [patientRefSuggestions, setPatientRefSuggestions] = useState([])
  const [devicesSuggestions, setDevicesSuggestions] = useState([])
  const [journeySuggestions, setJourneySuggestions] = useState([])

  const [valid, setValid] = useState(false)

  useEffect(() => {
    setValid(false)
    request('/info/app-codes/list').then(res => {
      setAppCodeSuggestions(res.data)
      if (isCluster) {
        request(`/info/journey/list?appCode=${res.data[0]}`).then(res =>
          setJourneySuggestions(res.data)
        )
      }
    })
    request('/info/devices/list').then(res => setDevicesSuggestions(res.data))
  }, [])

  const validateAppCode = createValidater({
    validater: appCode => appCodeSuggestions.includes(appCode),
    setError: setAppCodeError,
    errorMessage: 'Invalid App Code provided',
    setValid
  })

  const validateOffice = createValidater({
    validater: office =>
      officeSuggestions.map(val => val.title).includes(office),
    setError: setOfficeError,
    errorMessage: 'Invalid Office provided',
    setValid
  })

  const validatePatientRef = createValidater({
    validater: patientRef => patientRefSuggestions.includes(patientRef),
    setError: setPatientRefError,
    errorMessage: 'Invalid Patient Id provided',
    setValid
  })

  const validateDevices = createValidater({
    validater: device =>
      device.length == 0 ||
      devicesSuggestions.map(val => val.full_name).includes(device),
    setError: setDeviceError,
    errorMessage: 'Invalid Device provided',
    setValid
  })

  const validateJourney = createValidater({
    validater: journey =>
      journey.length == 0 ||
      journeySuggestions.map(val => val.journey_name).includes(journey),
    setError: setJourneyError,
    errorMessage: 'Invalid Journey provided',
    setValid
  })

  const updateOfficeSuggestions = appCode =>
    request(`/info/offices/list?appCode=${appCode}`).then(res =>
      setOfficeSuggestions(res.data)
    )

  const updateJourneySuggestions = appCode =>
    request(`/info/journey/list?appCode=${appCode}`).then(res =>
      setJourneySuggestions(res.data)
    )

  const updatePatientRefSuggestions = office =>
    request(
      `/info/patients/list?officeId=${
        officeSuggestions.find(val => val.title === office)?.id
      }`
    ).then(res => setPatientRefSuggestions(res.data))

  const getJourneyId = journeyName => {
    const selectedJourneyData = journeySuggestions.filter(
      item => journeyName === item.journey_name
    )
    const selectedId =
      selectedJourneyData.length > 0 ? selectedJourneyData[0].JID : ''

    return selectedId
  }

  const url = generateUrl({
    baseUrl,
    isCluster,
    appCode,
    dates,
    officeId: officeSuggestions.find(val => val.title === office)?.id,
    patientRef,
    device: devicesSuggestions.find(val => val.full_name === device)
      ?.short_name,
    journeyId,
    showArchivedOffices
  })

  if (isCluster)
    return (
      <ModalCard href={url} color={color} useButton={false} {...props}>
        <Flex column separation="24px" xAlign="stretch">
          <Flex column separation="12px">
            <SectionHeader>Date Range:</SectionHeader>
            <DateSelector onChange={setDates} value={dates} color={color} />
          </Flex>
          {isDevice && (
            <Flex column separation="12px">
              <SectionHeader>Select Device:</SectionHeader>
              <AutoComplete
                value={device}
                onChange={selectedDevice => {
                  setDevice(selectedDevice)
                  validateDevices(selectedDevice, true)
                }}
                onBlur={() => validateDevices(device)}
                suggestions={devicesSuggestions.map(item => item.full_name)}
                placeholder="Start typing the device"
              />
              {deviceError.length > 0 && (
                <ErrorMessage>{deviceError}</ErrorMessage>
              )}
            </Flex>
          )}
          {includeJourneySelector && (
            <Flex column separation="12px">
              <SectionHeader>Select Journey:</SectionHeader>
              <AutoComplete
                value={journey}
                onChange={selectedJourney => {
                  setJourney(selectedJourney)
                  const selectedId = getJourneyId(selectedJourney)
                  setJourneyId(selectedId)
                  validateJourney(selectedJourney, true)
                }}
                onBlur={() => validateJourney(journey)}
                suggestions={journeySuggestions.map(item => item.journey_name)}
                placeholder="Start typing the journey name"
              />
              {journeyError.length > 0 && (
                <ErrorMessage>{journeyError}</ErrorMessage>
              )}
            </Flex>
          )}
        </Flex>
        <Button color={color} href={url}>
          Create Report
        </Button>
      </ModalCard>
    )

  return (
    <ModalCard href={url} color={color} useButton={false} {...props}>
      <Flex column separation="24px" xAlign="stretch">
        <Flex column separation="12px">
          <SectionHeader>Date Range:</SectionHeader>
          <DateSelector onChange={setDates} value={dates} color={color} />
        </Flex>
        <Flex column separation="12px">
          <SectionHeader>Select App Code:</SectionHeader>
          <AutoComplete
            value={appCode}
            onChange={appCode => {
              setAppCode(appCode)
              if (validateAppCode(appCode, true))
                updateOfficeSuggestions(appCode)
              updateJourneySuggestions(appCode)
            }}
            onBlur={() => validateAppCode(appCode)}
            suggestions={appCodeSuggestions}
            placeholder="Start typing your App Code"
          />
          {appCodeError.length > 0 && (
            <ErrorMessage>{appCodeError}</ErrorMessage>
          )}
        </Flex>
      </Flex>
      {isOffice && (
        <Flex column separation="12px">
          <SectionHeader>Select Office:</SectionHeader>
          <AutoComplete
            disabled={!(appCode.length > 0) || appCodeError.length > 0}
            value={office}
            onChange={office => {
              setOffice(office)
              if (validateOffice(office, true))
                updatePatientRefSuggestions(office)
            }}
            suggestions={officeSuggestions
              .filter(val =>
                showArchivedOffices ? true : val.archived === 'N'
              )
              .map(val => val.title)}
            placeholder="Start typing your Office"
          />
          {officeError.length > 0 && <ErrorMessage>{officeError}</ErrorMessage>}
        </Flex>
      )}
      {isPatient && (
        <Flex column separation="12px">
          <SectionHeader>Select Patient:</SectionHeader>
          <AutoComplete
            disabled={
              !(office.length > 0) ||
              officeError.length > 0 ||
              !(appCode.length > 0) ||
              appCodeError.length > 0
            }
            value={patientRef}
            onChange={patientRef => {
              setPatientRef(patientRef)
              validatePatientRef(patientRef, true)
            }}
            suggestions={patientRefSuggestions}
            placeholder="Start typing your Patient Id"
          />
          {patientRefError.length > 0 && (
            <ErrorMessage>{patientRefError}</ErrorMessage>
          )}
        </Flex>
      )}
      {isDevice && (
        <Flex column separation="12px">
          <SectionHeader>Select Device:</SectionHeader>
          <AutoComplete
            value={device}
            onChange={selectedDevice => {
              setDevice(selectedDevice)
              validateDevices(selectedDevice, true)
            }}
            onBlur={() => validateDevices(device)}
            suggestions={devicesSuggestions.map(item => item.full_name)}
            placeholder="Start typing the device"
          />
          {deviceError.length > 0 && <ErrorMessage>{deviceError}</ErrorMessage>}
        </Flex>
      )}
      {includeJourneySelector && (
        <Flex column separation="12px">
          <SectionHeader>Select Journey:</SectionHeader>
          <AutoComplete
            disabled={!(appCode.length > 0) || appCodeError.length > 0}
            value={journey}
            onChange={selectedJourney => {
              setJourney(selectedJourney)
              const selectedId = getJourneyId(selectedJourney)
              setJourneyId(selectedId)
              validateJourney(selectedJourney, true)
            }}
            onBlur={() => validateJourney(journey)}
            suggestions={journeySuggestions.map(item => item.journey_name)}
            placeholder="Start typing the journey name"
          />
          {journeyError.length > 0 && (
            <ErrorMessage>{journeyError}</ErrorMessage>
          )}
        </Flex>
      )}
      <Label separation="12px" yAlign="center">
        <Switch
          onChange={setShowArchivedOffices}
          checked={showArchivedOffices}
          uncheckedIcon={false}
          checkedIcon={false}
        />
        <span>Show Archived Offices</span>
      </Label>
      <Button disabled={!valid} href={url} color={color}>
        Create Report
      </Button>
    </ModalCard>
  )
}

function generateUrl({
  baseUrl,
  isCluster,
  appCode,
  dates,
  officeId,
  patientRef,
  device,
  journeyId,
  showArchivedOffices
}) {
  let url = `${baseUrl}/${appCode}`
  const query = {
    startDate: dates?.startDate?.getTime(),
    endDate: dates?.endDate?.getTime(),
    isCluster,
    officeId,
    patientRef,
    device,
    journeyId,
    showArchivedOffices
  }

  for (const key in query) {
    if (query[key] === undefined || query[key] === '') delete query[key]
  }

  const serializedQuery = new URLSearchParams(query).toString()
  if (serializedQuery.length > 0) url += '?' + serializedQuery

  return url
}
